"use client";

import { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { getFullName, getInitial } from "../../utils/urlUtils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Mail,
  Briefcase,
  GraduationCap,
  User,
  Wallet,
  Building2,
  CreditCard,
} from "lucide-react";

function formatAddress(address) {
  if (!address) return "—";
  const { street, city, state, pincode } = address;
  return [street, city, state, pincode].filter(Boolean).join(", ") || "—";
}

function getSubjectNames(teacherSubjects, allSubjects = []) {
  if (!teacherSubjects || teacherSubjects.length === 0) return "—";
  return teacherSubjects
    .map((s) => {
      if (typeof s === "object" && s.name) return s.name;
      const id = typeof s === "string" ? s : s?._id;
      const found = allSubjects.find((sub) => sub._id === id);
      return found ? found.name : id;
    })
    .join(", ");
}

function getClassTeacherLabel(teacher) {
  const ct =
    teacher.assignment?.assignments?.find((a) => a.type === "class_teacher") ||
    teacher.classAssignments?.find((a) => a.type === "class_teacher");
  if (ct)
    return `Class ${ct.class?.name ?? "—"} — Section ${ct.section?.name ?? "—"}`;
  if (teacher.classTeacherDetails)
    return `Class ${teacher.classTeacherDetails.className ?? "—"} — Section ${teacher.classTeacherDetails.sectionName ?? "—"}`;
  return "—";
}

function getWorkShiftName(teacher, allWorkShifts = []) {
  if (typeof teacher.workShift === "object" && teacher.workShift?.shiftName)
    return teacher.workShift.shiftName;
  if (teacher.workShiftName) return teacher.workShiftName;
  const id = typeof teacher.workShift === "string" ? teacher.workShift : teacher.workShift?._id;
  if (!id) return "—";
  const found = allWorkShifts.find((s) => s._id === id);
  return found?.shiftName ?? found?.name ?? "—";
}

/**
 * Read-only teacher profile view. Used in MyProfile (teacher self-view) and TeachersContent (admin view).
 * @param {Object} teacher - Teacher user object
 * @param {Array} [allSubjects] - Optional list of subjects to resolve subject IDs; if not passed, fetches when teacher has subjects
 */
export function TeacherProfileView({ teacher, allSubjects: allSubjectsProp }) {
  const [allSubjects, setAllSubjects] = useState(allSubjectsProp ?? []);
  const [allWorkShifts, setAllWorkShifts] = useState([]);

  useEffect(() => {
    if (Array.isArray(allSubjectsProp) && allSubjectsProp.length >= 0) {
      setAllSubjects(allSubjectsProp);
      return;
    }
    const fetchSubjects = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.subjects, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.data?.subjects ?? res.data?.subjects ?? [];
        setAllSubjects(Array.isArray(list) ? list : []);
      } catch {
        setAllSubjects([]);
      }
    };
    fetchSubjects();
  }, [allSubjectsProp]);

  useEffect(() => {
    const fetchWorkShifts = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axiosInstance.get(API_ENDPOINTS.workShifts, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.data?.shifts ?? res.data?.data?.workShifts ?? res.data?.shifts ?? [];
        setAllWorkShifts(Array.isArray(list) ? list : []);
      } catch {
        setAllWorkShifts([]);
      }
    };
    fetchWorkShifts();
  }, []);

  if (!teacher) return null;

  const photo = teacher.profileImage || teacher.profilePicture;
  const display = (v) => (v != null && v !== "" ? v : "—");
  const classTeacherLabel = getClassTeacherLabel(teacher);
  const hasBank =
    teacher.bankAccountDetails?.accountHolderName ||
    teacher.bankAccountDetails?.accountNumber;

  const academicYearLabel =
    teacher.academicYear?.startYear && teacher.academicYear?.endYear
      ? `${teacher.academicYear.startYear}-${teacher.academicYear.endYear}`
      : "—";

  const departmentLabel =
    typeof teacher.department === "object" && teacher.department?.name
      ? teacher.department.name
      : teacher.departmentName || teacher.department || "—";

  const workShiftLabel = getWorkShiftName(teacher, allWorkShifts);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pb-8 border-b border-slate-200">
        <Avatar className="h-24 w-24 rounded-2xl border-2 border-slate-200 shadow-sm">
          <AvatarImage src={photo} alt={getFullName(teacher)} />
          <AvatarFallback className="rounded-2xl bg-slate-100 text-slate-600 text-xl">
            {getInitial(teacher)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-800 truncate">
            {getFullName(teacher)}
          </h1>
          {teacher.username && (
            <p className="text-slate-500 text-sm mt-0.5">@{teacher.username}</p>
          )}
          <p className="text-slate-600 mt-1">
            {display(teacher.employeeCode || teacher.teacherCode)} ·{" "}
            {teacher.role === "teacher" ? "Teacher" : display(teacher.role)}
          </p>
          {classTeacherLabel !== "—" && (
            <p className="text-sm text-slate-600 mt-1">
              Class Teacher: {classTeacherLabel}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-8 mt-8">
        <Section title="Basic information" icon={User}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Teacher / Employee code" value={display(teacher.employeeCode || teacher.teacherCode)} />
            <Row label="Date of birth" value={teacher.birthday ? new Date(teacher.birthday).toLocaleDateString() : "—"} />
            <Row label="Gender" value={display(teacher.gender)} />
            <Row label="Blood group" value={display(teacher.bloodGroup)} />
            <Row label="Qualification" value={display(teacher.qualification)} />
            <Row label="Subjects" value={teacher.subjects?.length ? getSubjectNames(teacher.subjects, allSubjects) : "—"} />
            <Row label="Experience" value={display(teacher.experience)} />
            <Row label="Specialization" value={display(teacher.specialization)} />
          </dl>
        </Section>

        <Section title="Contact" icon={Mail}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Email" value={display(teacher.email)} />
            <Row label="Phone" value={display(teacher.phoneNumber)} />
            <Row label="Address" value={formatAddress(teacher.address)} />
          </dl>
        </Section>

        <Section title="Role & assignment" icon={Briefcase}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Role" value={teacher.role === "teacher" ? "Teacher" : display(teacher.role)} />
            <Row label="Class teacher" value={classTeacherLabel} />
            <Row label="Academic year" value={academicYearLabel} />
            <Row label="Additional roles" value={display(teacher.additionalRoles)} />
          </dl>
        </Section>

        <Section title="Employment & personal" icon={Building2}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="Designation" value={display(teacher.designation)} />
            <Row label="Department" value={departmentLabel} />
            <Row label="Mother's name" value={display(teacher.motherName)} />
            <Row label="Father's name" value={display(teacher.fatherName)} />
            <Row
              label="Date of joining"
              value={
                teacher.dateOfJoining
                  ? new Date(teacher.dateOfJoining).toLocaleDateString()
                  : "—"
              }
            />
            <Row label="Marital status" value={display(teacher.maritalStatus)} />
            <Row label="PAN number" value={display(teacher.panNumber)} />
            <Row label="Teacher type" value={display(teacher.teacherType)} />
            <Row label="Contract type" value={display(teacher.contractType)} />
          </dl>
        </Section>

        <Section title="Salary & work shift" icon={Wallet}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Row label="EPF" value={display(teacher.epf)} />
            <Row
              label="Basic salary"
              value={
                teacher.basicSalary != null
                  ? `₹${Number(teacher.basicSalary).toLocaleString()}`
                  : "—"
              }
            />
            <Row
              label="Allowance"
              value={
                teacher.allowance != null
                  ? `₹${Number(teacher.allowance).toLocaleString()}`
                  : "—"
              }
            />
            <Row label="Work shift" value={workShiftLabel} />
          </dl>
        </Section>

        {hasBank && (
          <Section title="Bank account" icon={CreditCard}>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Row label="Account holder" value={display(teacher.bankAccountDetails?.accountHolderName)} />
              <Row label="Account number" value={display(teacher.bankAccountDetails?.accountNumber)} />
              <Row label="Bank name" value={display(teacher.bankAccountDetails?.bankName)} />
              <Row label="IFSC" value={display(teacher.bankAccountDetails?.ifsc)} />
            </dl>
          </Section>
        )}

        {(teacher.notes != null && teacher.notes !== "") && (
          <Section title="Notes" icon={GraduationCap}>
            <p className="text-slate-700 whitespace-pre-wrap">{teacher.notes}</p>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        <h2 className="font-medium text-slate-800">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-800">{value}</dd>
    </div>
  );
}

export default TeacherProfileView;
